import {
  canAddUsersViaCSV,
  canRemoveUsersViaCSV,
  canSearchAccountUsers,
  canViewAccounts,
  canViewConfig,
  canViewCustomBrandingConfig,
  canViewDataDumps,
  canViewGroups,
  canViewRolesAndPermissions,
  canViewMarketplace,
  canViewPerformAdmin,
  canViewSubaccounts,
  canViewTags,
} from '@get-bridge/learn-legacy-shared/dist/utils/canViewPermissions';
import { productEnabled } from '@get-bridge/learn-legacy-shared/dist/utils/accountConfig';
import can from '@get-bridge/learn-legacy-shared/dist/utils/can';
import isFeatureEnabled from '@get-bridge/learn-legacy-shared/dist/utils/isFeatureEnabled';
import Runtime from '@get-bridge/learn-legacy-shared/dist/core/Runtime';

export default {
  general: (hosts, adminOnRuntime) => {
    const host = adminOnRuntime ? hosts.perform : hosts.learn;
    return {
      id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL',
      name: I18n.t('General'),
      sections: !host
        ? []
        : [
            canViewConfig() && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_ACCOUNT_SETTINGS',
              name: I18n.t('Account Settings'),
              href: '/admin/config',
              otherHrefs: ['/admin/config/.*'],
              host,
            },
            canViewCustomBrandingConfig() && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_CUSTOM_BRANDING',
              name: I18n.t('Custom Branding'),
              href: '/admin/branding',
              host,
            },
            can('notification_type_view') && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_NOTIFICATIONS',
              name: I18n.t('Notifications'),
              href: '/admin/notifications',
              host,
            },
            canSearchAccountUsers() && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_SEARCH_USERS_ACROSS_ACCOUNTS',
              name: I18n.t('Search Users Across Accounts'),
              href: '/admin/support/user-accounts',
              host,
            },
            canViewAccounts() && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_MANAGE_ACCOUNTS',
              name: I18n.t('Manage Accounts'),
              href: '/admin/support/accounts',
              otherHrefs: ['/admin/support/accounts/.*'],
              host,
            },
            canViewSubaccounts() && {
              id: 'ADMIN_ACCOUNT_MANAGEMENT_GENERAL_SUBACCOUNTS',
              name: I18n.t('Subaccounts'),
              href: '/admin/sub-accounts',
              otherHrefs: ['/admin/sub-accounts/.*'],
              host,
            },
          ].filter(Boolean),
      quickActions: [],
    }
  },
  usersAndPermissions: (hosts, adminOnRuntime) => {
    const host = adminOnRuntime ? hosts.perform : hosts.learn;
    return {
      id: 'ADMIN_USERS_GROUP',
      name: I18n.t('Users & permissions'),
      sections: !host
        ? []
        : [
            can('user_view') && {
              id: 'ADMIN_USERS_GROUP_MANAGE_USERS',
              name: I18n.t('Manage Users'),
              href: '/admin/users',
              otherHrefs: ['/admin/learner/.*'],
              host,
            },
            canViewGroups() && {
              id: 'ADMIN_USERS_GROUP_MANAGE_GROUPS',
              name: I18n.t('Manage Groups'),
              href: '/admin/groups',
              otherHrefs: ['/admin/groups/.*'],
              host,
            },
            canViewRolesAndPermissions() && {
              id: 'ADMIN_USERS_GROUP_MANAGE_PERMISSIONS',
              name: I18n.t('Roles & Permissions'),
              href: '/admin/permissions',
              host,
            },
            can('user_masquerade') && {
              id: 'ADMIN_USERS_GROUP_MANAGE_MASQUERADE',
              name: I18n.t('Masquerade'),
              href: '/admin/masquerade',
              host,
            },
          ].filter(Boolean),
      quickActions: !host
        ? []
        : [
            canAddUsersViaCSV() && {
              id: 'ADMIN_USERS_GROUP_ADD_VIA_CSV',
              name: I18n.t('Add Users via CSV'),
              href: '/admin/users/upload?uploadType=import',
              host,
            },
            canRemoveUsersViaCSV() && {
              id: 'ADMIN_USERS_GROUP_REMOVE_VIA_CSV',
              name: I18n.t('Remove Users via CSV'),
              href: '/admin/users/upload?uploadType=deport',
              host,
            },
            can('user_data_export') && {
              id: 'ADMIN_USERS_GROUP_EXPORT',
              name: I18n.t('Export all users'),
              href: '/download/new?type=report&info[id]=UsersReport',
              host,
            },
          ].filter(Boolean),
    }
  },
  tools: (hosts, adminOnRuntime) => {
    const host = adminOnRuntime ? hosts.perform : hosts.learn;
    return {
      id: 'ADMIN_TOOLS',
      name: I18n.t('Tools'),
      sections: !host
        ? []
        : [
            canViewDataDumps() && {
              id: 'ADMIN_TOOLS_DOWNLOAD_ALL_DATA',
              name: I18n.t('Download All Data'),
              host,
              href: '/author/data-dumps',
            },
            can('import_object_view_all') && {
              id: 'ADMIN_TOOLS_VIEW_CSV_IMPORT_LOGS',
              name: I18n.t('View CSV Import Logs'),
              host,
              href: '/admin/csvlogs',
              otherHrefs: ['/admin/csvlog/\\d+'],
            },
            (can('account_self_view') || can('sub_account_view')) && {
              id: 'ADMIN_TOOLS_BULK_UPLOAD_HISTORICAL_ENROLLMENTS',
              name: I18n.t('Bulk Upload Historical Course Enrollments'),
              host,
              href: '/author/course/enrollments/upload',
            },
            isFeatureEnabled('learn_insights_autocsv_to_admin_tools') &&
              can('user_auto_csv') &&
              Runtime.get('DOMAIN.config.autoCsv') && {
                id: 'ADMIN_TOOLS_AUTO_CSV',
                name: I18n.t('Auto CSV'),
                host,
                href: '/admin/auto-csv',
              },
            {
              id: 'LTI_TOOLS_PLACEHOLDER_ADMIN_NAV',
              name: '',
              host,
              href: '',
            },
          ].filter(Boolean),
      quickActions: [].filter(Boolean),
    }
  },
  performance: (hosts, user) => ({
    id: 'ADMIN_PERFORMANCE',
    name: I18n.t('Performance'),
    sections:
      hosts.perform && canViewPerformAdmin(user)
        ? [
            {
              id: 'ADMIN_PERFORMANCE_1ON1',
              name: I18n.t('1on1 Settings'),
              host: hosts.perform,
              href: '/talent/settings/1on1/general',
            },
            {
              id: 'ADMIN_PERFORMANCE_GROUP_AGENDAS',
              name: I18n.t('Group Agendas'),
              host: hosts.perform,
              href: '/talent/settings/group-agendas',
            },
            isFeatureEnabled('perform_goals_admin_settings') && {
              id: 'ADMIN_PERFORMANCE_GOALS',
              name: I18n.t('Goals'),
              okrName: I18n.t('Objectives'),
              host: hosts.perform,
              href: '/talent/settings/goals',
            },
            {
              id: 'ADMIN_PERFORMANCE_ASSESSMENT',
              name: I18n.t('Feedback Settings'),
              host: hosts.perform,
              href: '/talent/settings/assessment/general',
            },
            {
              id: 'ADMIN_PERFORMANCE_TALENT_REVIEW',
              name: I18n.t('Talent Review'),
              host: hosts.perform,
              href: '/talent/settings/talent-review/general',
            },
            {
              id: 'ADMIN_PERFORMANCE_TIMELINE',
              name: I18n.t('Employee History'),
              host: hosts.perform,
              href: '/talent/settings/timeline',
            },
            {
              id: 'ADMIN_PERFORMANCE_PERFORMANCE',
              name: I18n.t('Performance Conversations'),
              host: hosts.perform,
              href: '/talent/settings/performance/general',
            },
          ].filter(Boolean)
        : [],
    quickActions: [],
  }),
  learning: (hosts, adminOnRuntime) => {
    const host = adminOnRuntime ? hosts.perform : hosts.learn;
    return {
      id: 'ADMIN_LEARNING',
      name: I18n.t('Learning'),
      sections: !host
        ? []
        : [
            canViewMarketplace() && {
              id: 'ADMIN_LEARNING_MARKETPLACE',
              name: I18n.t('Content Marketplace'),
              host,
              href: '/admin/markets',
              otherHrefs: ['/admin/markets/.*'],
            },
            canViewTags() && {
              id: 'ADMIN_LEARNING_TAGS',
              name: I18n.t('Manage Tags'),
              host,
              href: '/admin/tags',
              otherHrefs: ['/admin/tags/\\d+'],
            },
            can('category_view') && {
              id: 'ADMIN_LEARNING_CATEGORIES',
              name: I18n.t('Manage Categories'),
              host,
              href: '/admin/categories',
              otherHrefs: ['/admin/category/\\d+'],
            },
            isFeatureEnabled('learn_tag_type_credit') && can('credit_view') && {
              id: 'ADMIN_LEARNING_CREDITS',
              name: I18n.t('Manage Credit Types'),
              host,
              href: '/admin/credits',
              otherHrefs: ['/admin/credits/\\d+'],
            },
          ].filter(Boolean),
      quickActions: [],
    }
  },
  engagement: hosts => ({
    id: 'ADMIN_ENGAGEMENT',
    name: I18n.t('Engagement'),
    sections:
      hosts.perform && productEnabled('engage') && can('survey_view')
        ? [
            {
              id: 'ADMIN_ENGAGEMENT_MANAGE_QUESTIONS',
              name: I18n.t('Manage Questions'),
              host: hosts.perform,
              href: '/engage/questions',
            },
            {
              id: 'ADMIN_ENGAGEMENT_MANAGE_FACTORS',
              name: I18n.t('Manage Factors'),
              host: hosts.perform,
              href: '/engage/factors',
            },
          ]
        : [],
    quickActions: [],
  }),
};



// WEBPACK FOOTER //
// ./packages/bridge-global-nav/src/utils/buildAtlasLinks/primaryLinks/admin.js